import React from "react";
import { TrendingUp, TrendingDown, Activity, Minus, HelpCircle } from "lucide-react";

export const RAG_STYLES = {
  green: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
  amber: "bg-amber-500/15 text-amber-300 border-amber-400/30",
  red: "bg-red-500/15 text-red-300 border-red-400/30",
  grey: "bg-slate-500/15 text-slate-300 border-slate-400/30",
};

export const RagBadge = ({ rag, className = "" }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-bold tracking-[0.14em] uppercase ${RAG_STYLES[rag] || RAG_STYLES.grey} ${className}`}
  >
    {rag || "grey"}
  </span>
);

const CONF_STYLES = {
  high: "text-emerald-300",
  medium: "text-teal-300",
  low: "text-amber-300",
  estimated: "text-slate-400",
  unverified: "text-slate-400",
};

export const ConfBadge = ({ level }) => {
  const l = level === "unverified" ? "estimated" : level;
  return (
    <span className={`text-[10px] font-semibold tracking-[0.12em] uppercase ${CONF_STYLES[l] || CONF_STYLES.estimated}`}>
      {l || "estimated"}
    </span>
  );
};

const TREND_ICONS = {
  improving: [TrendingUp, "text-emerald-300"],
  stable: [Minus, "text-slate-300"],
  deteriorating: [TrendingDown, "text-red-300"],
  volatile: [Activity, "text-amber-300"],
  insufficient_history: [HelpCircle, "text-slate-500"],
};

export const TrendBadge = ({ trend }) => {
  const [Icon, cls] = TREND_ICONS[trend] || TREND_ICONS.insufficient_history;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] ${cls}`}>
      <Icon className="w-3.5 h-3.5" />
      {(trend || "—").replace(/_/g, " ")}
    </span>
  );
};

export const ScoreRing = ({ score, size = 148, stroke = 9, label = "/ 100" }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = score == null ? 0 : Math.max(0, Math.min(100, score));
  const color = score == null ? "#475569" : score >= 75 ? "#34d399" : score >= 50 ? "#fbbf24" : "#f87171";
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct / 100)}
          style={{ transition: "stroke-dashoffset 1s ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-serif text-white leading-none" style={{ fontSize: size / 3.4 }}>
          {score == null ? "—" : score}
        </div>
        <div className="text-[9px] tracking-[0.2em] uppercase text-white/45 mt-1">{label}</div>
      </div>
    </div>
  );
};

export const Kicker = ({ children, className = "" }) => (
  <div className={`text-[10px] tracking-[0.3em] font-bold uppercase text-teal-300 ${className}`}>{children}</div>
);

export const Panel = ({ children, className = "", ...rest }) => (
  <div className={`rounded-xl border border-white/10 bg-[#0c1626] ${className}`} {...rest}>
    {children}
  </div>
);

export const EmptyNote = ({ children }) => (
  <div className="text-sm text-white/40 italic py-6 text-center">{children}</div>
);

export const DataTable = ({ headers, rows, testId }) => (
  <div className="overflow-x-auto" data-testid={testId}>
    <table className="w-full text-[13px]">
      <thead>
        <tr className="text-left text-[10px] tracking-[0.14em] uppercase text-white/40 border-b border-white/10">
          {headers.map((h, hi) => (
            <th key={`${h}-${hi}`} className="px-3 py-2.5 font-semibold whitespace-nowrap">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-white/5 align-top hover:bg-white/[0.02]">
            {r.map((c, j) => (
              <td key={j} className="px-3 py-2.5 text-white/70">{c ?? "—"}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const LEVEL_COLOR = { high: "text-red-300", medium: "text-amber-300", low: "text-emerald-300" };
